<template>
  <div>
    <CustomerDataForm
      hide-header
      :read-only-phone="readOnlyContact"
      :read-only-contact-email="readOnlyContact"
      contact-email
      :email-tooltip="!booking.customer ? t('emailInformation') : undefined"
    />

    <div class="mt-4 grid gap-4 md:flex md:justify-end">
      <CVButton
        v-if="isDocumentScanningEnabled"
        variant="secondary"
        class="col-span-2"
        @click.prevent="$emit('scan-documents')"
      >
        <CameraIcon />
        {{ t('scanDocuments') }}</CVButton
      >
      <CVButton
        v-if="hasChanged"
        variant="default"
        :is-loading="isSaving"
        @click.prevent="onSubmit"
        >{{ t('save') }}</CVButton
      >
      <CVButton
        v-if="hasChanged"
        variant="secondary"
        :disabled="isSaving"
        @click.prevent="onCancel"
        >{{ t('cancel') }}</CVButton
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { cloneDeep } from 'lodash';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import { customerHandoverSchema } from '@/validation/customer.schema';
import type { Booking } from '@/entities/bookings/booking.entity';
import CustomerDataForm from '@/components/booking-forms/customer/CustomerDataForm.vue';
import { toTypedSchema } from '@vee-validate/yup';
import { CameraIcon } from 'lucide-vue-next';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useUpdateBooking } from '@/queries/use-bookings.ts';
import { useVatIdValidation } from '@/hooks/use-vat-id-validation';
import CVButton from '@/components/buttons/CVButton.vue';
import { fromCalendarDate } from '@/utils/calendar-date';

const props = defineProps<{
  booking: Booking;
  contactEmailOptional?: boolean;
  readOnlyContact?: boolean;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
  (e: 'scan-documents'): void;
}>();

const { t } = useI18n();

const isDocumentScanningEnabled = useFeatureFlag('documents-scanning');
const { isValidatingVatId, validateVatId } = useVatIdValidation();

const { isPending: isUpdatingBooking, mutateAsync: updateBooking } = useUpdateBooking();

const isSaving = computed(() => isUpdatingBooking.value || isValidatingVatId.value);

const initializeFormValues = () => {
  return {
    ...props.booking.customerData,
    dateOfBirth: props.booking.customerData?.dateOfBirth
      ? fromCalendarDate(props.booking.customerData.dateOfBirth)
      : undefined,
    idCardDate: props.booking.customerData?.idCardDate
      ? fromCalendarDate(props.booking.customerData.idCardDate)
      : undefined,
    licenseDate: props.booking.customerData?.licenseDate
      ? fromCalendarDate(props.booking.customerData.licenseDate)
      : undefined,
    contactEmail:
      props.booking.customerData?.contactEmail?.trim() ||
      props.booking.customer?.email?.trim() ||
      undefined,
  };
};

const onCancel = () => {
  const values = initializeFormValues();
  setValues(values);
  unchangedValues.value = cloneDeep(values);
};

watch(
  () => props.booking.customerData,
  () => onCancel(),
);

const validationSchema = toTypedSchema(
  customerHandoverSchema(
    computed(() => props.booking.bookedCar.vehicleType.minAge),
    computed(() => props.booking.startDate),
    computed(() => !props.contactEmailOptional),
    computed(() => !props.readOnlyContact),
  ),
);

const {
  handleSubmit,
  values: formValues,
  setValues,
  validate,
} = useForm({
  initialValues: initializeFormValues(),
  validationSchema,
});

const unchangedValues = ref(cloneDeep(formValues));

const onSubmit = handleSubmit(async (values) => {
  if (!(await validateVatId(values.vatId, props.booking.customerData?.vatId, values.country)))
    return;
  await updateBooking({
    id: props.booking.id,
    booking: { customerData: values },
  });
  unchangedValues.value = cloneDeep(formValues);
});

const hasChanged = useFormHasChanged(unchangedValues, formValues);
watch(hasChanged, () => emit('hasChanged', hasChanged.value));

defineExpose({
  validate,
  setValues,
});
</script>

<i18n lang="json">
{
  "en": {
    "emailInformation": "The email will be used to create a new customer account on handover. The contract, car-swap protocol, and NPS survey will be sent to this address. Please double check if the email is correct.",
    "cancel": "Cancel",
    "scanDocuments": "Scan Documents"
  },
  "de": {
    "emailInformation": "Die E-Mail wird bei der Übergabe verwendet, um einen neuen Kundenaccount zu erstellen. Vertrag, Tauschprotokoll und NPS-Umfrage werden an diese Adresse gesendet. Bitte stelle sicher, dass die E-Mail korrekt ist.",
    "cancel": "Abbrechen",
    "scanDocuments": "Dokumente scannen"
  },
  "es": {
    "emailInformation": "El correo se usará para crear una cuenta de cliente nueva en la entrega. El contrato, el protocolo de cambio de coche y la encuesta NPS se enviarán a esta dirección. Compruebe que el correo es correcto.",
    "cancel": "Cancelar",
    "scanDocuments": "Escanear documentos"
  }
}
</i18n>
